import React, { useState, useEffect, useRef } from 'react';
import { Table, Tag, Space, Button, Progress, Typography, Tooltip, Alert, Collapse, Input, Switch, Popconfirm, message } from 'antd';
import { useSubscription, useMutation } from '@apollo/react-hooks';
import CAMPAIGN_ORDER_SUBSCRIPTION from '../../../../core/GraphQl/Subscriptions/CAMPAIGN_ORDER_SUBSCRIPTION';
import {
  LoadingOutlined,
  EyeOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  StopOutlined,
  InfoCircleOutlined,
  DownOutlined,
  RightOutlined,
  RocketOutlined,
  InboxOutlined
} from '@ant-design/icons';
import { useHistory, useLocation } from 'react-router-dom';
import { useCampaignOrderQueueContext } from '../../../../core/components/campaigns/campaignordersqueue/useCampaignOrderQueueContext';
import useCampaignLaunchProgress from '../../../../core/hooks/useCampaignLaunchProgress';
import { createEnhancedSearchFilter } from '../../../../core/utils/enhancedSearch';
import { useCampaignRequestorContext } from '../../../../core/components/campaignRequestor/useCampaignRequestorContext';
import EDIT_CAMPAIGN_STATUS from '../../../../core/GraphQl/Mutations/EDIT_CAMPAIGN_STATUS';

const { Text } = Typography;
const { Panel } = Collapse;
const { Search } = Input;

// Helper function to safely render error items (handles both string and ErrorDetail object formats)
const renderErrorItem = (item) => {
  // If item is a string, return it as-is (backward compatibility)
  if (typeof item === 'string') {
    return item;
  }

  // If item is an ErrorDetail object, format it as a string
  if (item && typeof item === 'object' && item.name && item.message) {
    return `${item.name} (${item.message})`;
  }

  // Fallback: stringify the item
  return String(item);
};

// Component to render launch summary details
const LaunchSummaryDetails = ({ launchSummary }) => {
  if (!launchSummary) {
    return (
      <Alert
        message="No Launch Summary Available"
        description="This campaign was launched before the detailed tracking feature was implemented."
        type="info"
        showIcon
        icon={<InfoCircleOutlined />}
      />
    );
  }

  // Handle new structure: { [campaignId]: { summary, lineItemDetails, ... } }
  // If launchSummary has campaign ID keys, flatten and merge all summaries
  const isNewStructure = !launchSummary.summary && !launchSummary.lineItemDetails;

  let summary, lineItemDetails, overallSummary, timestamp, elapsedTimeFormatted, creativeSummary;

  if (isNewStructure) {
    // New structure: merge all campaign summaries
    const campaignSummaries = Object.values(launchSummary);

    // Merge all lineItemDetails from all campaigns
    lineItemDetails = campaignSummaries.reduce((acc, campaignSummary) => {
      return acc.concat(campaignSummary.lineItemDetails || []);
    }, []);

    // Calculate combined summary from all campaigns
    const totalLineItems = campaignSummaries.reduce((sum, cs) => sum + (cs.totalLineItems || 0), 0);
    const successfulLineItems = campaignSummaries.reduce((sum, cs) => sum + (cs.successfulLineItems || 0), 0);
    const failedLineItems = campaignSummaries.reduce((sum, cs) => sum + (cs.failedLineItems || 0), 0);
    const totalCreatives = campaignSummaries.reduce((sum, cs) => sum + (cs.totalCreatives || 0), 0);
    const successfulCreatives = campaignSummaries.reduce((sum, cs) => sum + (cs.successfulCreatives || 0), 0);
    const failedCreatives = campaignSummaries.reduce((sum, cs) => sum + (cs.failedCreatives || 0), 0);
    const skippedCreatives = campaignSummaries.reduce((sum, cs) => sum + (cs.skippedCreatives || 0), 0);

    summary = {
      totalLineItems,
      successfulLineItems,
      failedLineItems,
      totalCreatives,
      successfulCreatives,
      failedCreatives,
      skippedCreatives,
      successRate: totalCreatives > 0 ? `${Math.round((successfulCreatives / totalCreatives) * 100)}%` : 'N/A',
      lineItemSuccessRate: totalLineItems > 0 ? `${Math.round((successfulLineItems / totalLineItems) * 100)}%` : 'N/A'
    };

    // Use the latest timestamp
    timestamp = campaignSummaries.reduce((latest, cs) => {
      const csTimestamp = new Date(cs.timestamp);
      return !latest || csTimestamp > latest ? csTimestamp : latest;
    }, null);

    // Use the latest elapsedTimeFormatted
    elapsedTimeFormatted = campaignSummaries[campaignSummaries.length - 1]?.elapsedTimeFormatted;

    // Merge creative summaries
    // Handle both new ErrorDetail format and legacy string format
    creativeSummary = {
      successful: campaignSummaries.reduce((acc, cs) => acc.concat(cs.creativeSummary?.successful || []), []),
      // Use backward-compatible string summaries if available (preferred for display)
      failed: campaignSummaries.reduce((acc, cs) => {
        const failedItems = cs.creativeSummary?.failedSummary || cs.creativeSummary?.failed || [];
        return acc.concat(failedItems);
      }, []),
      skipped: campaignSummaries.reduce((acc, cs) => {
        const skippedItems = cs.creativeSummary?.skippedSummary || cs.creativeSummary?.skipped || [];
        return acc.concat(skippedItems);
      }, []),
      // Also merge the full ErrorDetail arrays for potential future use
      failedDetails: campaignSummaries.reduce((acc, cs) => acc.concat(cs.creativeSummary?.failed || []), []),
      skippedDetails: campaignSummaries.reduce((acc, cs) => acc.concat(cs.creativeSummary?.skipped || []), [])
    };

    overallSummary = creativeSummary;
  } else {
    // Old structure: use as-is, but ensure backward compatibility with string formats
    summary = launchSummary.summary;
    lineItemDetails = launchSummary.lineItemDetails;
    overallSummary = launchSummary.overallSummary;
    timestamp = launchSummary.timestamp;
    elapsedTimeFormatted = launchSummary.elapsedTimeFormatted;

    // Handle creativeSummary with backward compatibility
    const rawCreativeSummary = launchSummary.creativeSummary;
    creativeSummary = {
      successful: rawCreativeSummary?.successful || [],
      // Prefer string summaries if available, otherwise use the arrays
      failed: rawCreativeSummary?.failedSummary || rawCreativeSummary?.failed || [],
      skipped: rawCreativeSummary?.skippedSummary || rawCreativeSummary?.skipped || [],
      // Keep full details if available
      failedDetails: rawCreativeSummary?.failed || [],
      skippedDetails: rawCreativeSummary?.skipped || []
    };
  }

  // Fallback to creativeSummary if overallSummary is not present
  const effectiveOverallSummary = overallSummary || creativeSummary || { failed: [], skipped: [], successful: [] };

  return (
    <>
      <style>{`
        .nested-line-item > .ant-collapse-header {
          padding-left: 40px !important;
        }
      `}</style>
      <div style={{ padding: '12px 16px', backgroundColor: '#fafafa' }}>
        {/* Compact Overview - Single Row */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 12,
        padding: '8px 12px',
        backgroundColor: '#fff',
        borderRadius: '4px',
        border: '1px solid #f0f0f0'
      }}>
        <Space size="large">
          <Tooltip title="Total Creatives">
            <Space size="small">
              <InfoCircleOutlined style={{ fontSize: 16 }} />
              <Text strong style={{ fontSize: 16 }}>{summary.totalCreatives}</Text>
            </Space>
          </Tooltip>
          <Tooltip title="Successful">
            <Space size="small">
              <CheckCircleOutlined style={{ fontSize: 16, color: '#3f8600' }} />
              <Text strong style={{ fontSize: 16, color: '#3f8600' }}>{summary.successfulCreatives}</Text>
            </Space>
          </Tooltip>
          <Tooltip title="Failed">
            <Space size="small">
              <CloseCircleOutlined style={{ fontSize: 16, color: summary.failedCreatives > 0 ? '#cf1322' : '#8c8c8c' }} />
              <Text strong style={{ fontSize: 16, color: summary.failedCreatives > 0 ? '#cf1322' : '#000' }}>{summary.failedCreatives}</Text>
            </Space>
          </Tooltip>
          <Tooltip title="Skipped">
            <Space size="small">
              <StopOutlined style={{ fontSize: 16, color: summary.skippedCreatives > 0 ? '#faad14' : '#8c8c8c' }} />
              <Text strong style={{ fontSize: 16, color: summary.skippedCreatives > 0 ? '#faad14' : '#000' }}>{summary.skippedCreatives}</Text>
            </Space>
          </Tooltip>
          {elapsedTimeFormatted && (
            <Tooltip title="Launch Duration">
              <Space size="small">
                <ClockCircleOutlined style={{ fontSize: 16, color: '#1890ff' }} />
                <Text strong style={{ fontSize: 16, color: '#1890ff' }}>{elapsedTimeFormatted}</Text>
              </Space>
            </Tooltip>
          )}
        </Space>

        {/* Compact Success Rate */}
        <div style={{ flex: '0 0 350px', paddingLeft: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Text strong>Success Rate: {summary.successRate}</Text>
            <Progress
              percent={parseInt(summary.successRate)}
              status={parseInt(summary.successRate) === 100 ? 'success' : 'active'}
              strokeWidth={8}
              style={{ flex: 1, margin: 0 }}
            />
          </div>
        </div>
      </div>

      {/* Collapsible Line Item Details */}
      {lineItemDetails && lineItemDetails.length > 0 && (() => {
        // Group line items by campaign
        const groupedByCampaign = lineItemDetails.reduce((acc, lineItem) => {
          const campaignKey = lineItem.campaignId || lineItem.campaignName || 'default';
          if (!acc[campaignKey]) {
            acc[campaignKey] = {
              campaignId: lineItem.campaignId,
              campaignName: lineItem.campaignName,
              lineItems: [],
              totalCreatives: 0,
              successfulCreatives: 0,
              failedCreatives: 0,
              skippedCreatives: 0
            };
          }
          acc[campaignKey].lineItems.push(lineItem);
          acc[campaignKey].totalCreatives += lineItem.totalCreatives || 0;
          acc[campaignKey].successfulCreatives += lineItem.successfulCreatives || 0;
          acc[campaignKey].failedCreatives += lineItem.failedCreatives || 0;
          acc[campaignKey].skippedCreatives += lineItem.skippedCreatives || 0;
          return acc;
        }, {});

        const campaigns = Object.values(groupedByCampaign);
        const hasMultipleCampaigns = campaigns.length > 1;

        // Render function for a single line item
        const renderLineItem = (lineItem, index, isNested = false) => {
          const isFailedLineItem = lineItem.status === 'failed';

          return (
            <Panel
              key={index}
              className={isNested ? 'nested-line-item' : ''}
              header={
                <Space>
                  <Text strong>{lineItem.lineItemName || `Line Item ${index + 1}`}</Text>
                  {isFailedLineItem ? (
                    <Tag color="red" style={{ margin: 0 }}>
                      Failed to Create
                    </Tag>
                  ) : (
                    <Tag color="blue" style={{ margin: 0 }}>
                      {lineItem.successfulCreatives}/{lineItem.totalCreatives} Successful
                    </Tag>
                  )}
                </Space>
              }
            >
              <div style={{
                padding: '8px 12px',
                backgroundColor: '#fff',
                borderRadius: '4px',
                border: '1px solid #f0f0f0'
              }}>
                {/* Show error alert for failed line items */}
                {isFailedLineItem && lineItem.errorMessage && (
                  <Alert
                    message="Line Item Processing Error"
                    description={
                      <div>
                        <Text strong>{lineItem.lineItemName}</Text>
                        <div style={{ marginTop: 4, color: '#8c8c8c' }}>
                          {lineItem.errorMessage}
                        </div>
                      </div>
                    }
                    type="error"
                    showIcon
                    style={{ marginBottom: 12 }}
                  />
                )}

                {/* Compact Stats Row - only show for successful line items */}
                {!isFailedLineItem && (
                  <>
                    <Space size="large" style={{ marginBottom: 12 }}>
                      <Space size="small">
                        <InfoCircleOutlined />
                        <Text type="secondary" style={{ fontSize: 12 }}>Total:</Text>
                        <Text strong>{lineItem.totalCreatives}</Text>
                      </Space>
                      <Space size="small">
                        <CheckCircleOutlined style={{ color: '#3f8600' }} />
                        <Text type="secondary" style={{ fontSize: 12 }}>Successful:</Text>
                        <Text strong style={{ color: '#3f8600' }}>{lineItem.successfulCreatives}</Text>
                      </Space>
                      <Space size="small">
                        <CloseCircleOutlined style={{ color: lineItem.failedCreatives > 0 ? '#cf1322' : '#8c8c8c' }} />
                        <Text type="secondary" style={{ fontSize: 12 }}>Failed:</Text>
                        <Text strong style={{ color: lineItem.failedCreatives > 0 ? '#cf1322' : '#000' }}>{lineItem.failedCreatives}</Text>
                      </Space>
                      <Space size="small">
                        <StopOutlined style={{ color: lineItem.skippedCreatives > 0 ? '#faad14' : '#8c8c8c' }} />
                        <Text type="secondary" style={{ fontSize: 12 }}>Skipped:</Text>
                        <Text strong style={{ color: lineItem.skippedCreatives > 0 ? '#faad14' : '#000' }}>{lineItem.skippedCreatives}</Text>
                      </Space>
                    </Space>

                    {/* Issues */}
                    {lineItem.creativeDetails?.skipped?.length > 0 && (
                      <Alert
                        message={`Skipped Creatives (${lineItem.creativeDetails.skipped.length})`}
                        description={
                          <div style={{ maxHeight: 120, overflowY: 'auto' }}>
                            {lineItem.creativeDetails.skipped.map((item, i) => (
                              <div key={i} style={{ fontSize: 12, padding: '2px 0' }}>
                                <StopOutlined style={{ color: '#faad14', marginRight: 8 }} />
                                {renderErrorItem(item)}
                              </div>
                            ))}
                          </div>
                        }
                        type="warning"
                        showIcon={false}
                        style={{ marginTop: 8 }}
                      />
                    )}
                    {/* Failed Creatives */}
                    {lineItem.creativeDetails?.failed?.length > 0 && (
                      <Alert
                        message={`Failed Creatives (${lineItem.creativeDetails.failed.length})`}
                        description={
                          <div style={{ maxHeight: 120, overflowY: 'auto' }}>
                            {lineItem.creativeDetails.failed.map((item, i) => (
                              <div key={i} style={{ fontSize: 12, padding: '2px 0' }}>
                                <CloseCircleOutlined style={{ color: '#cf1322', marginRight: 8 }} />
                                {renderErrorItem(item)}
                              </div>
                            ))}
                          </div>
                        }
                        type="error"
                        showIcon={false}
                        style={{ marginTop: 8 }}
                      />
                    )}
                  </>
                )}
              </div>
            </Panel>
          );
        };

        // For single campaign without campaign info, render line items directly
        if (!hasMultipleCampaigns && campaigns.length === 1) {
          const campaign = campaigns[0];
          const hasCampaignInfo = campaign.campaignId || campaign.campaignName;

          // If no campaign info, render line items directly (avoids empty header)
          if (!hasCampaignInfo) {
            return (
              <Collapse
                ghost
                style={{ marginBottom: 8 }}
                defaultActiveKey={campaign.lineItems.length === 1 ? ['0'] : []}
              >
                {campaign.lineItems.map((lineItem, index) => renderLineItem(lineItem, index))}
              </Collapse>
            );
          }

          // Single campaign WITH campaign info - show campaign header
          return (
            <Collapse
              ghost
              style={{ marginBottom: 8 }}
              defaultActiveKey={['campaign-0']}
            >
              <Panel
                key="campaign-0"
                header={
                  <Space>
                    <Text strong style={{ color: '#1890ff' }}>
                      {campaign.campaignName || 'Campaign 1'}
                    </Text>
                    <Tag color="blue" style={{ margin: 0 }}>
                      {campaign.successfulCreatives}/{campaign.totalCreatives} Successful
                    </Tag>
                  </Space>
                }
              >
                <Collapse
                  ghost
                  defaultActiveKey={campaign.lineItems.length === 1 ? ['0'] : []}
                >
                  {campaign.lineItems.map((lineItem, index) => renderLineItem(lineItem, index, true))}
                </Collapse>
              </Panel>
            </Collapse>
          );
        }

        // For multiple campaigns, render with campaign grouping
        return (
          <Collapse
            ghost
            style={{ marginBottom: 8 }}
            defaultActiveKey={[]}
          >
            {campaigns.map((campaign, campaignIndex) => (
              <Panel
                key={`campaign-${campaignIndex}`}
                header={
                  <Space>
                    <Text strong style={{ color: '#1890ff' }}>
                      {campaign.campaignName || `Campaign ${campaignIndex + 1}`}
                    </Text>
                    <Tag color="blue" style={{ margin: 0 }}>
                      {campaign.successfulCreatives}/{campaign.totalCreatives} Successful
                    </Tag>
                  </Space>
                }
              >
                <Collapse
                  ghost
                  defaultActiveKey={campaign.lineItems.length === 1 ? ['0'] : []}
                >
                  {campaign.lineItems.map((lineItem, index) => renderLineItem(lineItem, index, true))}
                </Collapse>
              </Panel>
            ))}
          </Collapse>
        );
      })()}

      {/* Collapsible Issues Summary */}
      {(effectiveOverallSummary.failed.length > 0 || effectiveOverallSummary.skipped.length > 0) && (
        <Collapse ghost>
          <Panel
            header={
              <Space>
                <ExclamationCircleOutlined style={{ color: '#faad14' }} />
                <Text strong>Issues Summary</Text>
                <Tag color="warning">
                  {effectiveOverallSummary.failed.length + effectiveOverallSummary.skipped.length} Issue(s)
                </Tag>
              </Space>
            }
          >
            <div style={{
              padding: '8px 12px',
              backgroundColor: '#fff',
              borderRadius: '4px',
              border: '1px solid #f0f0f0'
            }}>
              {effectiveOverallSummary.failed.length > 0 && (
                <Alert
                  message={`All Failed Creatives (${effectiveOverallSummary.failed.length})`}
                  description={
                    <div style={{ maxHeight: 150, overflowY: 'auto' }}>
                      {effectiveOverallSummary.failed.map((item, i) => (
                        <div key={i} style={{ fontSize: 12, padding: '2px 0' }}>
                          <CloseCircleOutlined style={{ color: '#cf1322', marginRight: 8 }} />
                          {renderErrorItem(item)}
                        </div>
                      ))}
                    </div>
                  }
                  type="error"
                  showIcon={false}
                  style={{ marginBottom: effectiveOverallSummary.skipped.length > 0 ? 8 : 0 }}
                />
              )}
              {effectiveOverallSummary.skipped.length > 0 && (
                <Alert
                  message={`All Skipped Creatives (${effectiveOverallSummary.skipped.length})`}
                  description={
                    <div style={{ maxHeight: 150, overflowY: 'auto' }}>
                      {effectiveOverallSummary.skipped.map((item, i) => (
                        <div key={i} style={{ fontSize: 12, padding: '2px 0' }}>
                          <StopOutlined style={{ color: '#faad14', marginRight: 8 }} />
                          {renderErrorItem(item)}
                        </div>
                      ))}
                    </div>
                  }
                  type="warning"
                  showIcon={false}
                />
              )}
            </div>
          </Panel>
        </Collapse>
      )}

      {/* Compact Timestamp */}
      {timestamp && (
        <div style={{ marginTop: 8, textAlign: 'right' }}>
          <Text type="secondary" style={{ fontSize: 11 }}>
            Launched: {new Date(timestamp).toLocaleString()}
          </Text>
        </div>
      )}
      </div>
    </>
  );
};

// Utility function to get phase display config (no hooks)
const getPhaseDisplay = (currentPhase) => {
  const configs = {
    STARTING: { label: 'Starting', color: 'processing' },
    DATE_VALIDATION: { label: 'Date Validation', color: 'processing' },
    INSERTION_ORDER: { label: 'Insertion Order', color: 'processing' },
    PACKAGE: { label: 'Package Setup', color: 'processing' },
    LINE_ITEMS: { label: 'Line Items', color: 'processing' },
    CREATIVES: { label: 'Creatives', color: 'processing' },
    HEARTBEAT: { label: 'Processing', color: 'processing' },
    WARNING: { label: 'Warning', color: 'warning' },
    COMPLETED: { label: 'Completed', color: 'success' },
    FAILED: { label: 'Failed', color: 'error' }
  };
  return configs[currentPhase] || configs.STARTING;
};

// Progress tracker component for individual campaigns
const CampaignProgressTracker = ({ campaignOrderId, children }) => {
  const progressData = useCampaignLaunchProgress(campaignOrderId, {
    enabled: true,
    autoCleanup: false
  });

  return children(progressData);
};

export const CampaignOrderQueueProgrammatic = () => {
  const {
    campaignOrders,
    pagination,
    fetchCampaignOrdersQueue,
    loadingCampaignOrdersQueue,
    setPagination
  } = useCampaignOrderQueueContext();
  const { currentUserOrg } = useCampaignRequestorContext();

  const history = useHistory();
  const location = useLocation();
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMediaPlanStrategy, setFilterMediaPlanStrategy] = useState(false);
  const [archivingIds, setArchivingIds] = useState(new Set());
  const previousLaunchingIdsRef = useRef(new Set());
  const autoExpandedFailedCampaignsRef = useRef(new Set());

  // MUTATIONS
  const [updateCampaignOrderStatus] = useMutation(EDIT_CAMPAIGN_STATUS);

  // Parse URL parameters to detect recent launch
  const urlParams = new URLSearchParams(location.search);
  const launchedCampaignId = urlParams.get('launched');
  const launchTimestamp = urlParams.get('ts');

  // Function to fetch launching campaigns + completed programmatic campaigns with optional search
  const fetchLaunchingCampaigns = React.useCallback(({ skip = 0, first = pagination.pageSize, name = '', orgId, hasMediaPlanStrategy = false }) => {
    if (!orgId) return; // Don't fetch without orgId

    const enhancedSearchFilter = createEnhancedSearchFilter(name);
    const where = {
      AND: [
        {
          OR: [
            // Include campaigns currently launching
            { status_in: ['LAUNCHING_LIVE', 'LAUNCHING_PAUSED', 'LAUNCHING_ERROR'] },
            // Include campaigns that were launched programmatically and are now LIVE/PAUSED
            {
              AND: [
                { status_in: ['LIVE', 'PAUSED'] },
                { isLaunchProgrammatically: true }
              ]
            },
            // Include APPROVED campaigns that were launched programmatically (will filter errors client-side)
            {
              AND: [
                { status: 'APPROVED' },
                { isLaunchProgrammatically: true }
              ]
            }
          ]
        },
        // CRITICAL: Tenant security - filter by organization
        {
          orgs_some: {
            OR: [{ id: orgId }, { parentOrg_some: { id: orgId } }],
          },
        }
      ]
    };

    // Apply search filter if provided
    if (enhancedSearchFilter) {
      where.AND.push(enhancedSearchFilter);
    }

    // Filter by Has Media Plan Strategy
    if (hasMediaPlanStrategy) {
      where.AND.push({
        campaigns_some: {
          hasMediaPlanStrategy: true,
        },
      });
    }

    fetchCampaignOrdersQueue({
      variables: {
        first,
        skip,
        orderBy: 'updatedAt_DESC',
        where
      }
    });
  }, [pagination.pageSize, fetchCampaignOrdersQueue]);

  // Separate campaigns into launching and completed programmatic
  const launchingCampaigns = campaignOrders.filter(campaignOrder =>
    campaignOrder.status === 'LAUNCHING_LIVE' ||
    campaignOrder.status === 'LAUNCHING_PAUSED' ||
    campaignOrder.status === 'LAUNCHING_ERROR'
  );

  const completedProgrammaticCampaigns = campaignOrders.filter(campaignOrder =>
    (campaignOrder.status === 'LIVE' || campaignOrder.status === 'PAUSED') &&
    campaignOrder.isLaunchProgrammatically === true
  );

  // Include APPROVED campaigns that failed during programmatic launch (have error only, not successful launches)
  const failedProgrammaticCampaigns = campaignOrders.filter(campaignOrder =>
    campaignOrder.status === 'APPROVED' &&
    campaignOrder.isLaunchProgrammatically === true &&
    (campaignOrder.dspConfig?.status === 'ERROR' || campaignOrder.dspConfig?.error)
  );

  // Include APPROVED campaigns that have launch summaries (successful or partial launches that didn't transition status)
  const stuckApprovedProgrammaticCampaigns = campaignOrders.filter(campaignOrder =>
    campaignOrder.status === 'APPROVED' &&
    campaignOrder.isLaunchProgrammatically === true &&
    campaignOrder.dspConfig?.launchSummary &&
    !campaignOrder.dspConfig?.error &&
    campaignOrder.dspConfig?.status !== 'ERROR'
  );

  // All campaigns to display - prioritize launching, then failed, then completed, then stuck approved
  const allDisplayCampaigns = [...launchingCampaigns, ...failedProgrammaticCampaigns, ...completedProgrammaticCampaigns, ...stuckApprovedProgrammaticCampaigns];

  // Get IDs of currently launching campaigns for subscription
  const launchingCampaignIds = launchingCampaigns.map(c => c.id);

  // Subscribe to campaign updates for launching campaigns
  const { data: subscriptionData } = useSubscription(
    CAMPAIGN_ORDER_SUBSCRIPTION,
    {
      variables: {
        where: {
          mutation_in: ['UPDATED'],
          node: {
            id_in: launchingCampaignIds
          }
        }
      },
      skip: launchingCampaignIds.length === 0
    }
  );

  // Detect when a campaign completes launching and trigger refetch
  useEffect(() => {
    if (subscriptionData?.campaignOrder?.node) {
      const updatedCampaign = subscriptionData.campaignOrder.node;
      const campaignId = updatedCampaign.id;
      const newStatus = updatedCampaign.status;

      // Check if this campaign just completed or failed (transitioned from LAUNCHING_* to LIVE/PAUSED/APPROVED)
      const wasLaunching = previousLaunchingIdsRef.current.has(campaignId);
      const isNowComplete = newStatus === 'LIVE' || newStatus === 'PAUSED';
      const isNowFailed = newStatus === 'APPROVED' && updatedCampaign.dspConfig?.status === 'ERROR';

      if (wasLaunching && (isNowComplete || isNowFailed)) {
        console.log('[Auto-refetch] Campaign completed/failed launching:', campaignId, 'status:', newStatus);

        if (currentUserOrg) {
          const userData = localStorage.getItem('userData');
          const orgId = userData ? JSON.parse(userData).role.org.id : null;
          const currentOrgId = orgId ?? currentUserOrg.id;

          // Refetch to get the latest data with launch summary
          fetchLaunchingCampaigns({
            skip: (pagination.current - 1) * pagination.pageSize,
            first: pagination.pageSize,
            name: searchTerm,
            orgId: currentOrgId
          });
        }

        // Auto-expand the completed campaign row to show summary
        setTimeout(() => {
          if (!expandedRowKeys.includes(campaignId)) {
            setExpandedRowKeys([...expandedRowKeys, campaignId]);
          }
        }, 500); // Small delay to ensure data is loaded
      }
    }
  }, [subscriptionData, currentUserOrg, fetchCampaignOrdersQueue, pagination, expandedRowKeys, searchTerm, fetchLaunchingCampaigns]);

  // Track currently launching campaign IDs
  const launchingIdsKey = launchingCampaignIds.join(',');
  useEffect(() => {
    previousLaunchingIdsRef.current = new Set(launchingCampaignIds);
  }, [launchingIdsKey, launchingCampaignIds]); // Only update when IDs actually change

  // Auto-expand rows with errors (only once per campaign)
  const failedCampaignIdsKey = failedProgrammaticCampaigns.map(c => c.id).join(',');
  useEffect(() => {
    const campaignsWithErrors = failedProgrammaticCampaigns
      .filter(c => c.dspConfig?.error || c.dspConfig?.status === 'ERROR')
      .map(c => c.id);

    if (campaignsWithErrors.length > 0) {
      // Only auto-expand campaigns that haven't been auto-expanded before
      const newFailedCampaigns = campaignsWithErrors.filter(
        id => !autoExpandedFailedCampaignsRef.current.has(id)
      );

      if (newFailedCampaigns.length > 0) {
        // Mark these campaigns as auto-expanded
        newFailedCampaigns.forEach(id => autoExpandedFailedCampaignsRef.current.add(id));

        // Add to expanded rows
        setExpandedRowKeys(prevKeys => [...new Set([...prevKeys, ...newFailedCampaigns])]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [failedCampaignIdsKey]); // Only trigger when failed campaign IDs change. Intentionally exclude failedProgrammaticCampaigns to prevent infinite loop

  const columns = [
    {
      title: 'Campaign Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space direction="vertical" size="small">
          <Text strong>{text}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.advertiser}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Launch Status',
      key: 'launchStatus',
      render: (_, record) => {
        return (
          <CampaignProgressTracker campaignOrderId={record.id}>
            {(progressData) => {
              const getStatusInfo = (status, isCompleted, isCompletedProgrammatic, isFailedProgrammatic, isStuckApprovedProgrammatic) => {
                // Show error status for failed programmatic campaigns
                if (isFailedProgrammatic) {
                  return { label: 'Launch Failed', color: 'error', icon: <ExclamationCircleOutlined /> };
                }

                // Show status for campaigns that launched but didn't transition from APPROVED
                if (isStuckApprovedProgrammatic) {
                  return { label: 'Launched (Pending Status Update)', color: 'warning', icon: <CheckCircleOutlined /> };
                }

                if (isCompletedProgrammatic) {
                  // Show completed programmatic campaign status
                  switch (status) {
                    case 'LIVE':
                      return { label: 'Launched → Live', color: 'success', icon: <CheckCircleOutlined /> };
                    case 'PAUSED':
                      return { label: 'Launched → Paused', color: 'default', icon: <CheckCircleOutlined /> };
                    default:
                      return { label: 'Launched', color: 'success', icon: <CheckCircleOutlined /> };
                  }
                }

                if (isCompleted) {
                  // When completed, show final status without loading icon
                  switch (status) {
                    case 'LAUNCHING_LIVE':
                      return { label: 'Launched → Live', color: 'success', icon: null };
                    case 'LAUNCHING_PAUSED':
                      return { label: 'Launched → Paused', color: 'default', icon: null };
                    case 'LAUNCHING_ERROR':
                      return { label: 'Launch Error', color: 'error', icon: <ExclamationCircleOutlined /> };
                    default:
                      return { label: 'Completed', color: 'success', icon: null };
                  }
                }

                // While launching, show loading states
                switch (status) {
                  case 'LAUNCHING_LIVE':
                    return { label: 'Launching → Live', color: 'processing', icon: <LoadingOutlined /> };
                  case 'LAUNCHING_PAUSED':
                    return { label: 'Launching → Paused', color: 'warning', icon: <LoadingOutlined /> };
                  case 'LAUNCHING_ERROR':
                    return { label: 'Launch Error', color: 'error', icon: <ExclamationCircleOutlined /> };
                  default:
                    return { label: 'Launching', color: 'processing', icon: <LoadingOutlined /> };
                }
              };

              const statusInfo = getStatusInfo(record.status, progressData.isCompleted, record.isCompletedProgrammatic, record.isFailedProgrammatic, record.isStuckApprovedProgrammatic);
              const phaseConfig = getPhaseDisplay(progressData.phase || (record.isCompletedProgrammatic || record.isFailedProgrammatic || record.isStuckApprovedProgrammatic ? 'COMPLETED' : 'STARTING'));

              return (
                <Space direction="vertical" size="small">
                  <Tag color={statusInfo.color} icon={statusInfo.icon}>
                    {statusInfo.label}
                  </Tag>
                  {!record.isCompletedProgrammatic && !record.isFailedProgrammatic && !record.isStuckApprovedProgrammatic && record.isCurrentlyLaunching && (
                    <Tag color={phaseConfig.color} size="small">
                      {phaseConfig.label}
                    </Tag>
                  )}
                  {(record.isCompletedProgrammatic || record.isFailedProgrammatic || record.isStuckApprovedProgrammatic) && record.launchSummary && (() => {
                    // Check if completed campaign has failures
                    const hasFailures =
                      record.launchSummary?.failedLineItems > 0 ||
                      record.launchSummary?.summary?.failedCreatives > 0 ||
                      (record.launchSummary?.summary?.successRate && parseInt(record.launchSummary.summary.successRate) < 100);

                    const tagColor = record.isFailedProgrammatic ? 'red' : (hasFailures ? 'orange' : 'green');
                    const tagText = record.isFailedProgrammatic ? '⚠ Error Details' : (hasFailures ? '⚠ With Errors' : '✓ Summary Available');

                    return (
                      <Tag color={tagColor} size="small">
                        {tagText}
                      </Tag>
                    );
                  })()}
                  {(progressData.isPossibleTimeout) && (
                    <Tooltip title="Launch is taking longer than expected">
                      <ExclamationCircleOutlined style={{ color: '#faad14' }} />
                    </Tooltip>
                  )}
                </Space>
              );
            }}
          </CampaignProgressTracker>
        );
      },
    },
    {
      title: 'Progress',
      dataIndex: 'progress',
      key: 'progress',
      render: (_, record) => {
        // For failed programmatic campaigns, show error status
        if (record.isFailedProgrammatic) {
          const launchTimestamp = record.launchSummary?.timestamp;
          const formattedDate = launchTimestamp
            ? new Date(launchTimestamp).toLocaleString()
            : null;

          return (
            <Space direction="vertical" size="small" style={{ minWidth: 120 }}>
              <Space size="small">
                <CloseCircleOutlined style={{ color: '#cf1322', fontSize: 16 }} />
                <Text strong style={{ color: '#cf1322' }}>Launch Failed</Text>
              </Space>
              {formattedDate && (
                <Text type="secondary" style={{ fontSize: '11px' }}>
                  {formattedDate}
                </Text>
              )}
            </Space>
          );
        }

        // For stuck approved programmatic campaigns, show completion status with warning
        if (record.isStuckApprovedProgrammatic) {
          const launchTimestamp = record.launchSummary?.timestamp;
          const formattedDate = launchTimestamp
            ? new Date(launchTimestamp).toLocaleString()
            : null;

          // Check for failures in launch summary
          const hasFailures =
            record.launchSummary?.failedLineItems > 0 ||
            record.launchSummary?.summary?.failedCreatives > 0 ||
            (record.launchSummary?.summary?.successRate && parseInt(record.launchSummary.summary.successRate) < 100);

          const statusConfig = hasFailures
            ? { icon: <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: 16 }} />, text: 'Launched with Errors', color: '#faad14' }
            : { icon: <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} />, text: 'Launched Successfully', color: '#52c41a' };

          return (
            <Space direction="vertical" size="small" style={{ minWidth: 120 }}>
              <Space size="small">
                {statusConfig.icon}
                <Text strong style={{ color: statusConfig.color }}>{statusConfig.text}</Text>
              </Space>
              {formattedDate && (
                <Text type="secondary" style={{ fontSize: '11px' }}>
                  {formattedDate}
                </Text>
              )}
            </Space>
          );
        }

        // For completed programmatic campaigns, show completion status
        if (record.isCompletedProgrammatic) {
          const launchTimestamp = record.launchSummary?.timestamp;
          const formattedDate = launchTimestamp
            ? new Date(launchTimestamp).toLocaleString()
            : null;

          // Check for failures in launch summary
          const hasFailures =
            record.launchSummary?.failedLineItems > 0 ||
            record.launchSummary?.summary?.failedCreatives > 0 ||
            (record.launchSummary?.summary?.successRate && parseInt(record.launchSummary.summary.successRate) < 100);

          const statusConfig = hasFailures
            ? { icon: <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: 16 }} />, text: 'Completed with Errors', color: '#faad14' }
            : { icon: <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} />, text: 'Completed', color: '#52c41a' };

          return (
            <Space direction="vertical" size="small" style={{ minWidth: 120 }}>
              <Space size="small">
                {statusConfig.icon}
                <Text strong style={{ color: statusConfig.color }}>{statusConfig.text}</Text>
              </Space>
              {formattedDate && (
                <Text type="secondary" style={{ fontSize: '11px' }}>
                  {formattedDate}
                </Text>
              )}
            </Space>
          );
        }

        // For launching campaigns, show real-time progress
        return (
          <CampaignProgressTracker campaignOrderId={record.id}>
            {(progressData) => {
              const progress = progressData.progress || 0;
              const message = progressData.message || 'Starting launch process...';
              const isFailed = progressData.isFailed || record.status === 'LAUNCHING_ERROR';
              const isCompleted = progressData.isCompleted || false;

              return (
                <Space direction="vertical" size="small" style={{ minWidth: 120 }}>
                  <Progress
                    percent={Math.max(0, progress)}
                    size="small"
                    status={isFailed ? 'exception' : isCompleted ? 'success' : 'active'}
                    strokeWidth={6}
                  />
                  <Text type="secondary" style={{ fontSize: '11px' }}>
                    {message}
                  </Text>
                </Space>
              );
            }}
          </CampaignProgressTracker>
        );
      },
    },
    {
      title: 'Elapsed Time',
      dataIndex: 'elapsedTime',
      key: 'elapsedTime',
      render: (_, record) => {
        // For completed, failed, or stuck approved campaigns, show persisted elapsed time from launch summary
        if (record.isCompletedProgrammatic || record.isFailedProgrammatic || record.isStuckApprovedProgrammatic) {
          const elapsedTime = record.launchSummary?.elapsedTimeFormatted || '-';
          const color = record.isFailedProgrammatic ? '#cf1322' : (elapsedTime !== '-' ? '#52c41a' : undefined);

          return (
            <Space>
              <ClockCircleOutlined style={{ color }} />
              <Text style={{ color }}>
                {elapsedTime}
              </Text>
            </Space>
          );
        }

        // For launching campaigns, show real-time elapsed time
        return (
          <CampaignProgressTracker campaignOrderId={record.id}>
            {(progressData) => {
              const elapsedTime = progressData.elapsedTimeFormatted || '-';

              return (
                <Space>
                  <ClockCircleOutlined />
                  <Text>{elapsedTime}</Text>
                </Space>
              );
            }}
          </CampaignProgressTracker>
        );
      },
    },
    {
      title: 'Media Plan',
      key: 'hasMediaPlanStrategy',
      render: (_, record) => {
        const hasStrategy = record.campaignOrder?.campaigns?.some(campaign => campaign.hasMediaPlanStrategy);
        return hasStrategy ? (
          <Tag color="purple">Strategy</Tag>
        ) : null;
      },
    },
    {
      title: 'Campaign Period',
      key: 'period',
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <Text style={{ fontSize: '12px' }}>
            <strong>Start:</strong> {record.startDate}
          </Text>
          <Text style={{ fontSize: '12px' }}>
            <strong>End:</strong> {record.endDate}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center',
      width: 120,
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={record.actions.viewDetails}
            />
          </Tooltip>
          <Tooltip title="Archive Campaign Order">
            <Popconfirm
              title="Are you sure you want to archive this campaign order?"
              description="This action cannot be undone. The campaign order will be moved to archived status."
              onConfirm={() => handleArchiveCampaignOrder(record.id)}
              okText="Yes, Archive"
              cancelText="Cancel"
              okType="danger"
            >
              <Button
                type="text"
                size="small"
                icon={<InboxOutlined />}
                loading={archivingIds.has(record.id)}
                danger
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Transform campaign data for display
  const tableData = allDisplayCampaigns.map(campaignOrder => {
    // Determine if this is a completed programmatic campaign
    const isCompletedProgrammatic =
      (campaignOrder.status === 'LIVE' || campaignOrder.status === 'PAUSED') &&
      campaignOrder.isLaunchProgrammatically === true;

    const isCurrentlyLaunching =
      campaignOrder.status === 'LAUNCHING_LIVE' ||
      campaignOrder.status === 'LAUNCHING_PAUSED' ||
      campaignOrder.status === 'LAUNCHING_ERROR';

    const isFailedProgrammatic =
      campaignOrder.status === 'APPROVED' &&
      campaignOrder.isLaunchProgrammatically === true &&
      (campaignOrder.dspConfig?.status === 'ERROR' || campaignOrder.dspConfig?.error);

    const isStuckApprovedProgrammatic =
      campaignOrder.status === 'APPROVED' &&
      campaignOrder.isLaunchProgrammatically === true &&
      campaignOrder.dspConfig?.launchSummary &&
      !campaignOrder.dspConfig?.error &&
      campaignOrder.dspConfig?.status !== 'ERROR';

    return {
      key: campaignOrder.id,
      id: campaignOrder.id,
      name: campaignOrder.name,
      advertiser: campaignOrder.advertiser?.name || '-',
      startDate: new Date(campaignOrder.startDate).toLocaleDateString(),
      endDate: new Date(campaignOrder.endDate).toLocaleDateString(),
      status: campaignOrder.status,
      campaignOrder: campaignOrder,
      dspConfig: campaignOrder.dspConfig,
      launchSummary: campaignOrder.dspConfig?.launchSummary,
      isCompletedProgrammatic,
      isCurrentlyLaunching,
      isFailedProgrammatic,
      isStuckApprovedProgrammatic,
      actions: {
        viewDetails: () => {
          history.push(`/campaigns/v2/orderqueue/details/${campaignOrder.id}`);
        }
      }
    };
  });

  // Table change handler for pagination
  const handleTableChange = ({ current, pageSize }, filters, sorter) => {
    if (!currentUserOrg) return;

    const userData = localStorage.getItem('userData');
    const orgId = userData ? JSON.parse(userData).role.org.id : null;
    const currentOrgId = orgId ?? currentUserOrg.id;

    setPagination((prev) => ({ ...prev, current, pageSize }));
    fetchLaunchingCampaigns({
      skip: (current - 1) * pageSize || 0,
      first: pageSize,
      name: searchTerm,
      orgId: currentOrgId,
      hasMediaPlanStrategy: filterMediaPlanStrategy,
    });
  };

  // Search handler
  const handleSearchChange = (value) => {
    if (!currentUserOrg) return;

    const userData = localStorage.getItem('userData');
    const orgId = userData ? JSON.parse(userData).role.org.id : null;
    const currentOrgId = orgId ?? currentUserOrg.id;

    setSearchTerm(value);
    fetchLaunchingCampaigns({
      skip: 0,
      first: pagination.pageSize,
      name: value,
      orgId: currentOrgId,
      hasMediaPlanStrategy: filterMediaPlanStrategy,
    });
  };

  // Media Plan Strategy filter handler
  const handleMediaPlanStrategyFilterChange = (checked) => {
    if (!currentUserOrg) return;

    const userData = localStorage.getItem('userData');
    const orgId = userData ? JSON.parse(userData).role.org.id : null;
    const currentOrgId = orgId ?? currentUserOrg.id;

    setFilterMediaPlanStrategy(checked);
    fetchLaunchingCampaigns({
      skip: 0,
      first: pagination.pageSize,
      name: searchTerm,
      orgId: currentOrgId,
      hasMediaPlanStrategy: checked,
    });
  };

  // Archive handler
  const handleArchiveCampaignOrder = async (campaignOrderId) => {
    if (!currentUserOrg) return;

    setArchivingIds((prev) => {
      const next = new Set(prev);
      next.add(campaignOrderId);
      return next;
    });

    try {
      await updateCampaignOrderStatus({
        variables: {
          id: campaignOrderId,
          status: 'ARCHIVED',
        },
      });

      message.success('Campaign order archived successfully');

      const userData = localStorage.getItem('userData');
      const orgId = userData ? JSON.parse(userData).role.org.id : null;
      const currentOrgId = orgId ?? currentUserOrg.id;

      fetchLaunchingCampaigns({
        skip: (pagination.current - 1) * pagination.pageSize,
        first: pagination.pageSize,
        name: searchTerm,
        orgId: currentOrgId,
        hasMediaPlanStrategy: filterMediaPlanStrategy,
      });
    } catch (error) {
      message.error('Failed to archive campaign order: ' + error.message);
    } finally {
      setArchivingIds((prev) => {
        const next = new Set(prev);
        next.delete(campaignOrderId);
        return next;
      });
    }
  };

  React.useEffect(() => {
    if (!currentUserOrg) return;

    const userData = localStorage.getItem('userData');
    const orgId = userData ? JSON.parse(userData).role.org.id : null;
    const currentOrgId = orgId ?? currentUserOrg.id;

    // Check if this is a recent launch (within last 10 seconds)
    const isRecentLaunch = launchTimestamp &&
      (Date.now() - parseInt(launchTimestamp)) < 10000;

    if (isRecentLaunch) {
      // For recent launches, wait 3 seconds then fetch to catch the launched campaign
      const refreshTimer = setTimeout(() => {
        fetchLaunchingCampaigns({
          skip: 0,
          first: pagination.pageSize,
          orgId: currentOrgId
        });
      }, 3000);

      // Clean URL after handling recent launch
      const urlCleanupTimer = setTimeout(() => {
        const newUrl = location.pathname;
        history.replace(newUrl);
      }, 5000);

      // Cleanup function for recent launch timers
      return () => {
        clearTimeout(refreshTimer);
        clearTimeout(urlCleanupTimer);
      };
    } else {
      // For normal visits, fetch immediately (no periodic refresh)
      fetchLaunchingCampaigns({
        skip: 0,
        first: pagination.pageSize,
        orgId: currentOrgId
      });
    }
  }, [currentUserOrg, fetchCampaignOrdersQueue, launchTimestamp, location.pathname, history, fetchLaunchingCampaigns, pagination.pageSize]);

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Space>
          <RocketOutlined style={{ color: '#1890ff', fontSize: '20px' }} />
          <Text strong>Programmatic Launches</Text>
          <Text type="secondary">
            ({launchingCampaigns.length} launching
            {failedProgrammaticCampaigns.length > 0 && `, ${failedProgrammaticCampaigns.length} failed`}
            {completedProgrammaticCampaigns.length > 0 && `, ${completedProgrammaticCampaigns.length} completed`}
            {stuckApprovedProgrammaticCampaigns.length > 0 && `, ${stuckApprovedProgrammaticCampaigns.length} pending status`})
          </Text>
          {launchedCampaignId && !allDisplayCampaigns.some(c => c.id === launchedCampaignId) && (
            <Text type="secondary" style={{ color: '#faad14' }}>
              • Looking for recently launched campaign...
            </Text>
          )}
        </Space>
        <Space size="middle">
          <Space size="small">
            <Switch
              checked={filterMediaPlanStrategy}
              onChange={handleMediaPlanStrategyFilterChange}
              size="small"
            />
            <span style={{ fontSize: '12px' }}>Has Media Plan Strategy</span>
          </Space>
          <Search
            placeholder="Search campaign"
            style={{ width: 200 }}
            onSearch={handleSearchChange}
            allowClear
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={tableData}
        loading={loadingCampaignOrdersQueue}
        pagination={pagination}
        onChange={handleTableChange}
        scroll={{ x: 1200 }}
        expandable={{
          expandedRowRender: (record) => <LaunchSummaryDetails launchSummary={record.launchSummary} />,
          expandedRowKeys: expandedRowKeys,
          onExpand: (expanded, record) => {
            if (expanded) {
              setExpandedRowKeys([...expandedRowKeys, record.key]);
            } else {
              setExpandedRowKeys(expandedRowKeys.filter(key => key !== record.key));
            }
          },
          expandIcon: ({ expanded, onExpand, record }) => {
            // Only show expand icon if launch summary exists
            if (!record.launchSummary) {
              return null;
            }

            return expanded ? (
              <Tooltip title="Hide launch details">
                <Button
                  type="text"
                  size="small"
                  icon={<DownOutlined />}
                  onClick={(e) => onExpand(record, e)}
                />
              </Tooltip>
            ) : (
              <Tooltip title="Show launch details">
                <Button
                  type="text"
                  size="small"
                  icon={<RightOutlined />}
                  onClick={(e) => onExpand(record, e)}
                  style={{
                    border: '1px solid #d9d9d9',
                    borderRadius: '4px'
                  }}
                />
              </Tooltip>
            );
          },
          rowExpandable: (record) => !!record.launchSummary,
        }}
      />
    </div>
  );
};